import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

const NAV = [
  { label: "Über", id: "about" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Exits", id: "exits" },
  { label: "Kontakt", id: "contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-300 border-b ${
        scrolled || open ? "bg-background/80 backdrop-blur-lg border-border/60 py-4" : "bg-transparent border-transparent py-6"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-8 flex items-center justify-between">
        <button onClick={() => go(null)} className="group flex items-center" data-testid="header-logo-button">
          <span className="font-display text-xl font-medium text-foreground group-hover:text-[hsl(var(--accent-ink))] transition-colors">MKSL Consulting</span>
        </button>
        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}-link`}
              className="relative text-sm text-muted-foreground hover:text-foreground transition-colors after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:origin-right after:scale-x-0 after:bg-foreground after:transition-transform after:duration-500 hover:after:origin-left hover:after:scale-x-100">
              {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => go("contact")} data-testid="header-cta-button"
          className="hidden md:inline-flex h-9 items-center rounded-md border border-foreground/20 px-4 text-sm font-medium transition-colors hover:bg-foreground hover:text-background">
          Projekt anfragen
        </button>
        <button onClick={() => setOpen(!open)} className="md:hidden p-2" aria-label="Menü öffnen" data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="md:hidden absolute top-full inset-x-0 bg-background border-b border-border p-6 flex flex-col gap-2" data-testid="mobile-menu">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${n.id}-link`}
                className="text-left font-display text-2xl text-foreground py-2">{n.label}</button>
            ))}
            <button onClick={() => go("contact")} data-testid="mobile-cta-button"
              className="mt-4 h-12 rounded-lg bg-primary text-primary-foreground font-medium">Projekt anfragen</button>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
